/**
 * Nudgesmart Secure Frontend Logger
 * 
 * Provides sanitized, environment-controlled logging that:
 * - Strips PII and sensitive data automatically
 * - Sends critical logs to Gadget backend
 * - Respects development/production environments
 * - Provides category-based filtering
 * - Offers performance-optimized no-op in production
 */

class NudgesmartLogger {
  constructor() {
    // Environment detection using Vite's import.meta.env
    this.isDevelopment = import.meta.env.DEV;
    this.isDebugMode = this.isDevelopment || this.getDebugMode();
    
    // Rate limiting for backend logging
    this.backendLogQueue = [];
    this.lastBackendFlush = 0;
    this.BACKEND_FLUSH_INTERVAL = 5000; // 5 seconds
    this.MAX_BACKEND_LOGS_PER_INTERVAL = 10;
    
    // Hardcoded blacklist for noisy categories
    this.blacklistedCategories = new Set(['template-processing', 'preview', 'popup-settings']);
    
    // Categories for filtering
    this.enabledCategories = this.getEnabledCategories();
    
    // Performance optimization: bind methods once
    this.debug = this.debug.bind(this);
    this.info = this.info.bind(this);
    this.warn = this.warn.bind(this);
    this.error = this.error.bind(this);
  }

  /**
   * Get debug mode from localStorage or URL params (environment-aware)
   */
  getDebugMode() {
    try {
      // In production, only check localStorage (no URL params)
      if (import.meta.env.PROD) {
        const storageDebug = localStorage.getItem('nudgesmart_debug');
        // In production, require explicit 'production_debug_enabled' value
        return storageDebug === 'production_debug_enabled';
      }
      
      // In development, check multiple sources
      // 1. localStorage (highest priority)
      const storageDebug = localStorage.getItem('nudgesmart_debug');
      if (storageDebug === 'true') return true;
      if (storageDebug === 'false') return false;
      
      // 2. sessionStorage
      const sessionDebug = sessionStorage.getItem('nudgesmart_debug');
      if (sessionDebug === 'true') return true;
      
      // 3. URL parameter (development only)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('nudgesmart_debug') === 'true') {
        // Auto-save to session for this session
        sessionStorage.setItem('nudgesmart_debug', 'true');
        return true;
      }
      
      return false;
    } catch (e) {
      // Storage access failed
      return false;
    }
  }

  /**
   * Get enabled categories from localStorage
   */
  getEnabledCategories() {
    try {
      const stored = localStorage.getItem('nudgesmart_debug_categories');
      if (stored) {
        const categories = JSON.parse(stored);
        return new Set(categories);
      }
    } catch {
      // Fall through to default
    }
    
    // Default to all categories in development
    return this.isDevelopment ? new Set(['*']) : new Set();
  }

  /**
   * Sanitize data to remove PII and sensitive information
   */
  sanitizeData(data) {
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }
    
    if (data && typeof data === 'object') {
      const sanitized = {};
      
      for (const [key, value] of Object.entries(data)) {
        const lowerKey = key.toLowerCase();
        
        // Skip sensitive fields entirely
        if (this.isSensitiveField(lowerKey)) {
          sanitized[key] = '[REDACTED]';
          continue;
        }
        
        // Recursively sanitize nested objects
        sanitized[key] = this.sanitizeData(value);
      }
      
      return sanitized;
    }
    
    return data;
  }

  /**
   * Check if a field name indicates sensitive data
   */
  isSensitiveField(fieldName) {
    const sensitivePatterns = [
      'password', 'token', 'key', 'secret', 'apikey', 'api_key',
      'email', 'mail', 'phone', 'ssn', 'creditcard', 'card',
      'auth', 'session', 'cookie', 'jwt', 'bearer',
      'code', 'coupon', 'couponcode'
    ];

    return sensitivePatterns.some(pattern => fieldName.includes(pattern));
  }

  /**
   * Sanitize strings to remove email addresses and other PII
   */
  sanitizeString(str) {
    if (typeof str !== 'string') return str;
    
    // Replace email addresses
    str = str.replace(/[\w\.-]+@[\w\.-]+\.\w+/g, '[EMAIL]');
    
    // Replace potential API keys (long alphanumeric strings)
    str = str.replace(/\b[A-Za-z0-9]{32,}\b/g, '[API_KEY]');
    
    // Replace JWT tokens
    str = str.replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[JWT_TOKEN]');
    
    return str;
  }

  /**
   * Check if logging is enabled for a category
   */
  shouldLog(category, level) {
    // Check blacklist first - skip these categories entirely
    if (this.blacklistedCategories.has(category)) {
      return false;
    }
    
    // Always log errors
    if (level === 'error') return true;
    
    // Check if category is enabled
    if (this.enabledCategories.has('*') || this.enabledCategories.has(category)) {
      return true;
    }
    
    return false;
  }

  /**
   * Send log to backend (rate limited)
   */
  async sendToBackend(level, category, message, data) {
    // Only send warn and error to backend
    if (!['warn', 'error'].includes(level)) return;
    
    const now = Date.now();
    
    // Add to queue
    this.backendLogQueue.push({
      timestamp: now,
      level,
      category,
      message: this.sanitizeString(message),
      data: this.sanitizeData(data),
      url: window.location.href,
      userAgent: navigator.userAgent
    });
    
    // Flush queue if needed
    if (now - this.lastBackendFlush > this.BACKEND_FLUSH_INTERVAL || 
        this.backendLogQueue.length >= this.MAX_BACKEND_LOGS_PER_INTERVAL) {
      await this.flushBackendLogs();
    }
  }

  /**
   * Flush queued logs to backend
   */
  async flushBackendLogs() {
    if (this.backendLogQueue.length === 0) return;
    
    const logs = this.backendLogQueue.splice(0, this.MAX_BACKEND_LOGS_PER_INTERVAL);
    this.lastBackendFlush = Date.now();
    
    try {
      await fetch('/api/frontend-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs }),
      });
    } catch (error) {
      // Fallback to console.error if backend logging fails
      console.error('Failed to send logs to backend:', error);
    }
  }

  /**
   * Debug logging (development only)
   */
  debug(message, data = null, category = 'general') {
    if (!this.isDebugMode || !this.shouldLog(category, 'debug')) return;
    
    const sanitizedData = this.sanitizeData(data);
    console.log(`🔍 [${category.toUpperCase()}] ${message}`, sanitizedData);
  }

  /**
   * Info logging (important state changes)
   */
  info(message, data = null, category = 'general') {
    if (!this.shouldLog(category, 'info')) return;
    
    const sanitizedData = this.sanitizeData(data);
    
    if (this.isDebugMode) {
      console.log(`ℹ️ [${category.toUpperCase()}] ${message}`, sanitizedData);
    }
    
    // Send to backend for tracking
    this.sendToBackend('info', category, message, sanitizedData);
  }

  /**
   * Warning logging (issues that should be tracked)
   */
  warn(message, data = null, category = 'general') {
    const sanitizedData = this.sanitizeData(data);
    
    if (this.isDebugMode) {
      console.warn(`⚠️ [${category.toUpperCase()}] ${message}`, sanitizedData);
    }
    
    // Always send warnings to backend
    this.sendToBackend('warn', category, message, sanitizedData);
  }

  /**
   * Error logging (critical issues)
   */
  error(message, data = null, category = 'general') {
    const sanitizedData = this.sanitizeData(data);
    
    // Always show errors in console (even in production)
    console.error(`❌ [${category.toUpperCase()}] ${message}`, sanitizedData);
    
    // Always send errors to backend
    this.sendToBackend('error', category, message, sanitizedData);
  }

  /**
   * Enable/disable logging for specific categories
   */
  enableCategory(category) {
    this.enabledCategories.add(category);
    this.saveCategoriesToStorage();
  }

  disableCategory(category) {
    this.enabledCategories.delete(category);
    this.saveCategoriesToStorage();
  }

  /**
   * Enable all logging
   */
  enableAll() {
    this.enabledCategories.add('*');
    this.saveCategoriesToStorage();
  }

  /**
   * Disable all logging
   */
  disableAll() {
    this.enabledCategories.clear();
    this.saveCategoriesToStorage();
  }

  /**
   * Save enabled categories to localStorage
   */
  saveCategoriesToStorage() {
    try {
      localStorage.setItem(
        'nudgesmart_debug_categories',
        JSON.stringify(Array.from(this.enabledCategories))
      );
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Get current logging status
   */
  getStatus() {
    return {
      isDevelopment: this.isDevelopment,
      isDebugMode: this.isDebugMode,
      enabledCategories: Array.from(this.enabledCategories),
      backendQueueLength: this.backendLogQueue.length,
      lastBackendFlush: new Date(this.lastBackendFlush).toISOString()
    };
  }
}

// Create global logger instance
const logger = new NudgesmartLogger();

// Override console.log to filter out logs from blacklisted files
const originalConsoleLog = console.log;
const originalConsoleError = console.error;

// Blacklisted file patterns for console.log
const blacklistedFilePatterns = [
  'fieldHelpers.js',
  'templateUtils.js',
  'usePreviewContent.js'
];

// Function to check if log should be suppressed based on stack trace
const shouldSuppressConsoleLog = () => {
  try {
    const stack = new Error().stack;
    if (stack) {
      // Check if any blacklisted file appears in the stack trace
      return blacklistedFilePatterns.some(pattern => stack.includes(pattern));
    }
  } catch (e) {
    // If we can't get stack trace, allow the log
  }
  return false;
};

// Override console.log
console.log = function(...args) {
  if (!shouldSuppressConsoleLog()) {
    originalConsoleLog.apply(console, args);
  }
};

// Override console.error for fieldHelpers.js
console.error = function(...args) {
  // Check if it's the specific error from fieldHelpers.js
  const stack = new Error().stack;
  if (stack && stack.includes('fieldHelpers.js') && args[0]?.includes?.('Error generating Template Library fields')) {
    // Suppress this specific error
    return;
  }
  originalConsoleError.apply(console, args);
};

// Add global debug controls with environment awareness
if (typeof window !== 'undefined') {
  if (import.meta.env.DEV) {
    // Development: Full debug controls
    window.Nudgesmart = window.Nudgesmart || {};
    window.Nudgesmart.debug = {
      enable: () => {
        localStorage.setItem('nudgesmart_debug', 'true');
        logger.enableAll();
        console.log('✅ Nudgesmart debug logging enabled');
      },
      disable: () => {
        localStorage.setItem('nudgesmart_debug', 'false');
        logger.disableAll();
        console.log('❌ Nudgesmart debug logging disabled');
      },
      filter: (category) => {
        logger.disableAll();
        logger.enableCategory(category);
        console.log(`🔍 Nudgesmart debug logging filtered to: ${category}`);
      },
      status: () => {
        const status = logger.getStatus();
        console.table(status);
        return status;
      },
      flush: async () => {
        await logger.flushBackendLogs();
        console.log('📤 Nudgesmart logs flushed to backend');
      }
    };
  } else {
    // Production: Minimal safe interface
    window.Nudgesmart = window.Nudgesmart || {};
    window.Nudgesmart.reportIssue = (message) => {
      // Safe error reporting for production
      if (typeof message === 'string' && message.length < 500) {
        logger.error('User reported issue', { message }, 'user-report');
      }
    };
    // No debug controls exposed in production
  }
}

export default logger;